import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Modal, Space, Spin } from 'antd';
import {
  IntradayTiersError,
  loadIntradayTiers,
  saveIntradayTiers,
  loadEodTiers,
  saveEodTiers,
} from './intradayTiersApi';
import type { IntradayTierSettings as Settings } from './intradayTiersApi';

function TierSettingsDialog({
  onClose,
  onSaved,
  mode,
}: {
  onClose: () => void;
  onSaved: () => void;
  mode: 'intraday' | 'eod';
}) {
  const [settings, setSettings] = useState<Settings>();
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef(true);
  const savePending = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setSettings(undefined);
    setError('');
    setConflict(false);
    const load = mode === 'intraday' ? loadIntradayTiers : loadEodTiers;
    load(controller.signal)
      .then(result => {
        if (controller.signal.aborted) return;
        setSettings(result);
        setSelected(
          result.tiers
            .filter(tier => tier[`${mode}_enabled`])
            .map(tier => tier.id),
        );
      })
      .catch(err => {
        if (!controller.signal.aborted)
          setError(err.message || 'Tier settings could not be loaded.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [attempt, mode]);

  const changed = settings?.tiers.some(
    tier => selected.includes(tier.id) !== Boolean(tier[`${mode}_enabled`]),
  );
  const save = async () => {
    if (!settings || savePending.current) return;
    savePending.current = true;
    setSaving(true);
    setError('');
    try {
      const saveTiers = mode === 'intraday' ? saveIntradayTiers : saveEodTiers;
      await saveTiers(settings.version, selected);
      onSaved();
      if (mounted.current) onClose();
    } catch (err) {
      if (!mounted.current) return;
      setError(
        err instanceof Error
          ? err.message
          : 'Tier settings could not be saved.',
      );
      setConflict(err instanceof IntradayTiersError && err.status === 409);
    } finally {
      savePending.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <Modal
      open
      title={mode === 'intraday' ? 'Intraday tiers' : 'EoD tiers'}
      onCancel={() => {
        if (!savePending.current) onClose();
      }}
      closable={!saving}
      footer={[
        <Button key="cancel" disabled={saving} onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={loading || !changed || conflict}
          onClick={save}
        >
          Save
        </Button>,
      ]}
    >
      <p>
        Choose catalogue tiers to fetch{' '}
        {mode === 'intraday' ? 'intraday' : 'EoD'} prices automatically.
        Supported assets in playing or paused real-time sessions, including
        their performance benchmarks, continue to fetch regardless of this
        selection.
      </p>
      {mode === 'eod' && (
        <p style={{ fontSize: 12, color: '#666' }}>
          Futures, yields, T-bills and options are not supported for automatic
          EoD fetching. Synthetic AFS bonds are excluded. CDS and repo daily
          capture also requires intraday eligibility. Required FX spots are
          included automatically and count toward the EoD total.
        </p>
      )}
      <p>
        With no tiers selected, only session demand is fetched. Changes take
        effect on the next scheduled run.
      </p>
      {loading && <Spin aria-label="Loading tier settings" />}
      {settings && (
        <Space orientation="vertical" style={{ width: '100%' }}>
          {settings.tiers.map(tier => (
            <div key={tier.id}>
              <Checkbox
                checked={selected.includes(tier.id)}
                disabled={saving || conflict}
                onChange={event =>
                  setSelected(current =>
                    event.target.checked
                      ? [...current, tier.id]
                      : current.filter(id => id !== tier.id),
                  )
                }
              >
                {tier.label}
              </Checkbox>
              {!tier.effective_active && (
                <span style={{ color: '#666' }}>
                  {' '}
                  (Inactive{tier.active ? ' ancestor' : ''}; no catalogue
                  fetching)
                </span>
              )}
            </div>
          ))}
          {!settings.tiers.length && <p>No catalogue tiers are available.</p>}
        </Space>
      )}
      {error && (
        <div role="alert" style={{ marginTop: 12 }}>
          <p>{error}</p>
          {(!settings || conflict) && (
            <Button onClick={() => setAttempt(value => value + 1)}>
              Reload settings
            </Button>
          )}
        </div>
      )}
    </Modal>
  );
}

export default function IntradayTierSettings({
  onSaved,
  mode = 'intraday',
}: {
  onSaved: () => void;
  mode?: 'intraday' | 'eod';
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>
        {mode === 'intraday' ? 'Intraday tiers' : 'EoD tiers'}
      </Button>
      {open && (
        <TierSettingsDialog
          mode={mode}
          onSaved={onSaved}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
